import { type QueryClient, queryOptions } from "@tanstack/react-query";

import type { ListCaseApiResponses } from "@recobra/schemas/case";
import type { TaskCaseSummary } from "@recobra/schemas/task";

import { casesApi } from "../lib/api";

type CaseSummary = ListCaseApiResponses["items"][number];

/** The case as a task form reads it: the list record flattened to the fields the summary shows. */
export const taskCaseSummaryOf = (kase: CaseSummary): TaskCaseSummary => ({
  caseType: kase.caseType,
  clientName: kase.clientName,
  branchName: kase.branchName,
  vehicle: kase.vehicle,
  liableVehicles: kase.liableVehicles ?? [],
  debtorName: kase.contacts?.find(({ type }) => type === "debtor")?.name,
});

/**
 * Looks for this case in any already-fetched cases query (detail page, list
 * page, ...) so a case-scoped task form can reuse it instead of firing a
 * fresh request. See https://tkdodo.eu/blog/seeding-the-query-cache.
 */
const seedFromCache = (
  queryClient: QueryClient,
  id: string,
): { data: CaseSummary; updatedAt: number } | undefined => {
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ["cases"] })) {
    const data = query.state.data;
    if (!data || typeof data !== "object") continue;

    if ("items" in data && Array.isArray(data.items)) {
      const match = (data.items as CaseSummary[]).find((item) => item.id === id);
      if (match) return { data: match, updatedAt: query.state.dataUpdatedAt };
      continue;
    }

    if ("id" in data && data.id === id) {
      return { data: data as CaseSummary, updatedAt: query.state.dataUpdatedAt };
    }
  }

  return undefined;
};

export const caseSummaryQueryOptions = (queryClient: QueryClient, id: string) =>
  queryOptions({
    queryKey: ["cases", "summary", id],
    queryFn: async () => (await casesApi.listCases({ id, limit: 1 })).items[0] ?? null,
    initialData: () => seedFromCache(queryClient, id)?.data,
    initialDataUpdatedAt: () => seedFromCache(queryClient, id)?.updatedAt,
  });
